import { createCapSolver } from "./modules/capChallenge";
import {
  readSmsOptInPrefillFromUrl,
  scrubPrefillParamsFromUrl,
} from "./modules/smsOptInPrefill";
import {
  getPhoneCountry,
  getPhoneNumber,
  initIntlPhone,
  isPhoneNumberValid,
} from "./modules/dynamicContentLoader";

(function () {
  const root = document.getElementById("sms-opt-in-root");
  if (!root) {
    return;
  }

  const form = document.getElementById("sms-opt-in-form");
  const fullNameInput = document.getElementById("sms-opt-in-full-name");
  const phoneInput = document.getElementById("sms-opt-in-phone");
  const submitButton = document.getElementById("sms-opt-in-submit");
  const errorEl = document.getElementById("sms-opt-in-error");
  const successEl = document.getElementById("sms-opt-in-success");
  const phoneErrorEl = document.getElementById("sms-opt-in-phone-error");
  const phoneStatusEl = document.getElementById("sms-opt-in-phone-status");

  const careInput = document.getElementById("sms-opt-in-care");
  const marketingInput = document.getElementById("sms-opt-in-marketing");
  const careWrap = document.getElementById("sms-opt-in-care-wrap");
  const marketingWrap = document.getElementById("sms-opt-in-marketing-wrap");
  const careHelper = document.getElementById("sms-opt-in-care-helper");
  const marketingHelper = document.getElementById("sms-opt-in-marketing-helper");

  if (
    !form ||
    !fullNameInput ||
    !phoneInput ||
    !submitButton ||
    !careInput ||
    !marketingInput
  ) {
    return;
  }

  const prefill = readSmsOptInPrefillFromUrl();
  scrubPrefillParamsFromUrl(prefill);

  if (prefill.name) {
    fullNameInput.value = prefill.name;
  }
  if (prefill.care === true) {
    careInput.checked = true;
  }
  if (prefill.marketing === true) {
    marketingInput.checked = true;
  }

  const text = {
    nameRequired: root.dataset.nameRequired || "",
    phoneRequired: root.dataset.phoneRequired || "",
    invalidPhone: root.dataset.invalidPhone || "",
    scopeRequired: root.dataset.scopeRequired || "",
    genericError: root.dataset.genericError || "",
    networkError: root.dataset.networkError || "",
    checkingStatus: root.dataset.checkingStatus || "",
    statusReady: root.dataset.statusReady || "",
    alreadyOptedIn: root.dataset.alreadyOptedIn || "",
    lockedHelper: root.dataset.lockedHelper || "",
    submitLabel: root.dataset.submitLabel || "",
    submittingLabel: root.dataset.submittingLabel || "",
    validUsPhone: root.dataset.validUsPhone || "",
    usOnlyPhone: root.dataset.usOnlyPhone || "",
    phoneValidatorLoading: root.dataset.phoneValidatorLoading || "",
  };

  const PHONE_INSTANCE_KEY = "itiSmsOptIn";

  const capSolver = createCapSolver({
    endpointInputId: "sms-opt-in-cap-api-endpoint",
    tokenInputId: "sms-opt-in-cap-token",
  });

  let lastStatusPhone = "";
  let lastStatus = null;
  let statusRequest = null;
  let statusRequestPhone = "";
  let lastObservedPhone = "";
  let phoneValidatorReady = false;

  function stripHtml(message) {
    const container = document.createElement("div");
    container.innerHTML = String(message || "");
    return (container.textContent || container.innerText || "").trim();
  }

  function safeMessage(message) {
    const clean = stripHtml(message);
    if (!clean) {
      return "";
    }
    if (
      clean.includes("Traceback") ||
      clean.includes("frappe.") ||
      clean.includes("pymysql.") ||
      clean.includes("File \"")
    ) {
      return "";
    }
    return clean;
  }

  function extractFrappeErrorMessage(payload) {
    const directMessage = payload?.message;
    if (typeof directMessage === "string" && directMessage.trim()) {
      return safeMessage(directMessage);
    }
    if (directMessage && typeof directMessage === "object") {
      if (typeof directMessage.message === "string") {
        return safeMessage(directMessage.message);
      }
      if (typeof directMessage._error_message === "string") {
        return safeMessage(directMessage._error_message);
      }
    }
    if (typeof payload?._error_message === "string") {
      return safeMessage(payload._error_message);
    }
    if (typeof payload?._server_messages === "string") {
      try {
        const serverMessages = JSON.parse(payload._server_messages);
        for (const entry of serverMessages) {
          if (typeof entry === "string") {
            try {
              const parsed = JSON.parse(entry);
              const clean = safeMessage(parsed?.message);
              if (clean) {
                return clean;
              }
            } catch {
              const clean = safeMessage(entry);
              if (clean) {
                return clean;
              }
            }
          } else if (typeof entry?.message === "string") {
            const clean = safeMessage(entry.message);
            if (clean) {
              return clean;
            }
          }
        }
      } catch {
        return safeMessage(payload._server_messages);
      }
    }
    return "";
  }

  function userSafeError(message) {
    const error = new Error(message || "");
    error.userSafe = true;
    return error;
  }

  function showError(message) {
    if (!errorEl) {
      return;
    }
    errorEl.textContent = safeMessage(message) || text.genericError;
  }

  function clearError() {
    if (errorEl) {
      errorEl.textContent = "";
    }
  }

  function showSuccess(message) {
    if (!successEl) {
      return;
    }
    successEl.textContent = safeMessage(message) || "";
  }

  function clearSuccess() {
    if (successEl) {
      successEl.textContent = "";
    }
  }

  function showPhoneError(message) {
    if (!phoneErrorEl) {
      showError(message);
      return;
    }
    phoneErrorEl.textContent = safeMessage(message) || text.genericError;
    phoneInput.classList.add("is-invalid");
  }

  function clearPhoneError() {
    if (phoneErrorEl) {
      phoneErrorEl.textContent = "";
    }
    phoneInput.classList.remove("is-invalid");
  }

  function setPhoneStatus(message) {
    if (phoneStatusEl) {
      phoneStatusEl.textContent = safeMessage(message) || "";
    }
  }

  function setSubmitting(isSubmitting) {
    submitButton.disabled = isSubmitting;
    submitButton.textContent = isSubmitting
      ? text.submittingLabel
      : text.submitLabel;
  }

  function getNormalizedPhone() {
    return phoneValidatorReady ? String(getPhoneNumber(PHONE_INSTANCE_KEY) || "").trim() : "";
  }

  function getSelectedPhoneCountry() {
    return getPhoneCountry(PHONE_INSTANCE_KEY) || "US";
  }

  function phoneInputHasValue() {
    return Boolean(String(phoneInput.value || "").trim());
  }

  function resetIfPhoneIdentityChanged() {
    const normalizedPhone = getNormalizedPhone();
    if (normalizedPhone !== lastObservedPhone) {
      lastObservedPhone = normalizedPhone;
      if (normalizedPhone !== lastStatusPhone) {
        resetStatusState({ keepChecks: true });
      }
    }
  }

  function validatePhoneForApi() {
    if (!phoneInputHasValue()) {
      showPhoneError(text.phoneRequired);
      return null;
    }
    if (!phoneValidatorReady) {
      showPhoneError(text.phoneValidatorLoading);
      return null;
    }
    if (getSelectedPhoneCountry() !== "US") {
      showPhoneError(text.usOnlyPhone);
      return null;
    }
    if (!isPhoneNumberValid(PHONE_INSTANCE_KEY)) {
      showPhoneError(text.invalidPhone || text.usOnlyPhone);
      return null;
    }

    const normalizedPhone = getNormalizedPhone();
    if (!normalizedPhone) {
      showPhoneError(text.invalidPhone || text.usOnlyPhone);
      return null;
    }

    clearPhoneError();
    return normalizedPhone;
  }

  function formatTemplate(template, values) {
    return String(template || "").replace(/\{([a-z_]+)\}/g, (_, key) => {
      return values[key] || "";
    });
  }

  function setScopeState({
    input,
    wrapper,
    helper,
    checked,
    disabled,
    helperText = "",
    stateClass = "",
  }) {
    input.checked = Boolean(checked);
    input.disabled = Boolean(disabled);
    input.setAttribute("aria-disabled", disabled ? "true" : "false");

    if (helper) {
      helper.textContent = helperText;
    }

    if (wrapper) {
      wrapper.classList.toggle("is-opted", stateClass === "opted");
      wrapper.classList.toggle("is-locked", stateClass === "locked");
    }
  }

  function resetStatusState({ keepChecks = false } = {}) {
    lastStatus = null;
    lastStatusPhone = "";
    setPhoneStatus("");

    setScopeState({
      input: careInput,
      wrapper: careWrap,
      helper: careHelper,
      checked: keepChecks ? careInput.checked : false,
      disabled: false,
    });
    setScopeState({
      input: marketingInput,
      wrapper: marketingWrap,
      helper: marketingHelper,
      checked: keepChecks ? marketingInput.checked : false,
      disabled: false,
    });
  }

  function applyStatus(status) {
    lastStatus = status;
    lastStatusPhone = String(status.e164 || getNormalizedPhone() || "").trim();
    lastObservedPhone = lastStatusPhone;

    const sourceNumber = status.source_number || "";
    const lockedScopes = new Set(status.locked_scopes || []);
    const lockedHelper = formatTemplate(text.lockedHelper, {
      source_number: sourceNumber,
    });

    if (lockedScopes.has("care")) {
      setScopeState({
        input: careInput,
        wrapper: careWrap,
        helper: careHelper,
        checked: false,
        disabled: true,
        helperText: lockedHelper,
        stateClass: "locked",
      });
    } else if (status.care_opted_in) {
      setScopeState({
        input: careInput,
        wrapper: careWrap,
        helper: careHelper,
        checked: true,
        disabled: true,
        helperText: text.alreadyOptedIn,
        stateClass: "opted",
      });
    } else {
      setScopeState({
        input: careInput,
        wrapper: careWrap,
        helper: careHelper,
        checked: careInput.checked,
        disabled: false,
      });
    }

    if (lockedScopes.has("marketing")) {
      setScopeState({
        input: marketingInput,
        wrapper: marketingWrap,
        helper: marketingHelper,
        checked: false,
        disabled: true,
        helperText: lockedHelper,
        stateClass: "locked",
      });
    } else if (status.marketing_opted_in) {
      setScopeState({
        input: marketingInput,
        wrapper: marketingWrap,
        helper: marketingHelper,
        checked: true,
        disabled: true,
        helperText: text.alreadyOptedIn,
        stateClass: "opted",
      });
    } else {
      setScopeState({
        input: marketingInput,
        wrapper: marketingWrap,
        helper: marketingHelper,
        checked: marketingInput.checked,
        disabled: false,
      });
    }

    setPhoneStatus(status.message || text.statusReady);
  }

  async function getCsrfToken() {
    const existing = String(
      (window.frappe && window.frappe.csrf_token) || ""
    ).trim();
    if (existing && existing !== "None") {
      return existing;
    }

    const response = await fetch("/api/method/zoni_edu.services.csrf_bootstrap", {
      method: "POST",
      credentials: "include",
      headers: { Accept: "application/json" },
    });
    const data = await response.json();
    const token = String(data?.message?.csrf_token || "").trim();
    if (window.frappe) {
      window.frappe.csrf_token = token;
    }
    return token;
  }

  async function postMethod(method, payload, { forceCap = true } = {}) {
    const csrfToken = await getCsrfToken();
    const headers = await capSolver.headers(
      {
        "Content-Type": "application/json",
        Accept: "application/json",
        "X-Frappe-CSRF-Token": csrfToken,
      },
      { force: forceCap }
    );

    const response = await fetch(`/api/method/${method}`, {
      method: "POST",
      credentials: "include",
      headers,
      body: JSON.stringify(payload),
    });

    let data = {};
    try {
      data = await response.json();
    } catch {
      data = {};
    }

    if (!response.ok) {
      throw userSafeError(extractFrappeErrorMessage(data) || text.genericError);
    }

    return data?.message || data || {};
  }

  async function checkStatus({ forceCap = false } = {}) {
    await phoneInitPromise;
    const phone = validatePhoneForApi();
    if (!phone) {
      return null;
    }

    if (statusRequest && phone === statusRequestPhone) {
      return statusRequest;
    }

    clearError();
    clearSuccess();
    setPhoneStatus(text.checkingStatus);
    statusRequestPhone = phone;

    statusRequest = (async () => {
      try {
        const result = await postMethod(
          "zoni_edu.zoni_edu.sms_opt_in.get_sms_opt_in_status",
          { phone_number: phone, phone_country: getSelectedPhoneCountry() },
          { forceCap }
        );

        if (result?.ok && result.is_valid) {
          applyStatus(result);
          return result;
        }

        resetStatusState({ keepChecks: true });
        showError(result?.message || text.genericError);
        return null;
      } catch (error) {
        resetStatusState({ keepChecks: true });
        showError(
          error.userSafe
            ? error.message
            : text.networkError || text.genericError
        );
        return null;
      } finally {
        statusRequest = null;
        statusRequestPhone = "";
      }
    })();

    return statusRequest;
  }

  function validateBeforeSubmit() {
    const fullName = String(fullNameInput.value || "").trim();

    if (!fullName) {
      showError(text.nameRequired);
      fullNameInput.focus();
      return false;
    }
    if (!validatePhoneForApi()) {
      phoneInput.focus();
      return false;
    }
    if (!careInput.checked && !marketingInput.checked) {
      showError(text.scopeRequired);
      return false;
    }
    return true;
  }

  function applyPrefilledPhone(instance) {
    if (!prefill.phone) {
      return;
    }

    if (instance && typeof instance.setNumber === "function") {
      instance.setNumber(prefill.phone);
    } else {
      phoneInput.value = prefill.phone;
    }

    phoneInput.dispatchEvent(new Event("input", { bubbles: true }));
    resetIfPhoneIdentityChanged();

    if (validatePhoneForApi()) {
      setPhoneStatus(text.validUsPhone);
      window.setTimeout(() => {
        checkStatus({ forceCap: false });
      }, 0);
    }
  }

  function buildSubmitPayload(normalizedPhone) {
    const payload = {
      full_name: String(fullNameInput.value || "").trim(),
      phone_number: normalizedPhone,
      phone_country: getSelectedPhoneCountry(),
      opt_in_care: careInput.checked ? 1 : 0,
      opt_in_marketing: marketingInput.checked ? 1 : 0,
    };

    if (prefill.hadPrefill) {
      payload.prefilled = 1;
      if (prefill.sourceApp) {
        payload.source_app = prefill.sourceApp;
      }
      if (prefill.sourceRef) {
        payload.source_ref = prefill.sourceRef;
      }
    }

    return payload;
  }

  function redirectToReturnUrlIfNeeded() {
    if (!prefill.returnUrl) {
      return;
    }

    window.setTimeout(() => {
      window.location.assign(prefill.returnUrl);
    }, 0);
  }

  async function waitForPhoneValidationReady(instance) {
    if (!instance) {
      return false;
    }

    if (instance.promise && typeof instance.promise.then === "function") {
      try {
        await instance.promise;
      } catch (error) {
        console.debug("SMS opt-in phone utils failed to load:", error);
        return false;
      }
    }

    return true;
  }

  const phoneInitPromise = initIntlPhone({
    selector: "#sms-opt-in-phone",
    instanceKey: PHONE_INSTANCE_KEY,
    errorSelector: "#sms-opt-in-phone-error",
    preferred: ["us"],
    initialCountry: "us",
    onlyCountries: ["us"],
    separateDialCode: true,
    formatOnDisplay: true,
    invalidMessage: text.invalidPhone,
    onValidityChange: (isValid) => {
      if (isValid) {
        clearPhoneError();
        setPhoneStatus(text.validUsPhone);
      } else if (phoneInputHasValue()) {
        setPhoneStatus("");
      }
      resetIfPhoneIdentityChanged();
    },
    onCountryChange: () => {
      resetIfPhoneIdentityChanged();
    },
  }).then(async (instance) => {
    const isReady = await waitForPhoneValidationReady(instance);
    phoneValidatorReady = isReady;
    if (!isReady) {
      showPhoneError(text.phoneValidatorLoading);
      return null;
    }

    resetIfPhoneIdentityChanged();
    applyPrefilledPhone(instance);
    return instance;
  }).catch((error) => {
    console.debug("SMS opt-in phone initialization failed:", error);
    phoneValidatorReady = false;
    showPhoneError(text.phoneValidatorLoading);
    return null;
  });

  phoneInput.addEventListener("input", () => {
    resetIfPhoneIdentityChanged();
  });

  phoneInput.addEventListener("blur", () => {
    if (phoneInputHasValue()) {
      checkStatus({ forceCap: false });
    }
  });

  form.addEventListener("submit", async (event) => {
    event.preventDefault();
    clearError();
    clearSuccess();

    await phoneInitPromise;
    if (!validateBeforeSubmit()) {
      return;
    }

    const normalizedPhone = validatePhoneForApi();
    if (!normalizedPhone) {
      phoneInput.focus();
      return;
    }

    setSubmitting(true);

    try {
      const result = await postMethod(
        "zoni_edu.zoni_edu.sms_opt_in.submit_sms_opt_in",
        buildSubmitPayload(normalizedPhone),
        { forceCap: true }
      );

      if (result?.ok) {
        showSuccess(result.message);
        applyStatus(result);
        form.scrollIntoView({ behavior: "smooth", block: "start" });
        redirectToReturnUrlIfNeeded();
      } else {
        showError(result?.message || text.genericError);
      }
    } catch (error) {
      showError(
        error.userSafe
          ? error.message
          : text.networkError || text.genericError
      );
    } finally {
      setSubmitting(false);
    }
  });
})();
